import {
  createElement,
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from 'react';
import { ContentClipboardState } from '@sas/nova-core/services/drive';

export interface ContentClipboardContextValue {
  /**
   * The current clipboard state
   */
  clipboardState: ContentClipboardState;
}

export const ContentClipboardContext = createContext<ContentClipboardContextValue>({
  clipboardState: { state: 'empty' },
});
export const ContentClipboardDispatch = createContext<
  Dispatch<SetStateAction<ContentClipboardContextValue>> | undefined
>(undefined);

/**
 * Returns the current clipboard state for the nearest provider, or undefined if not under a
 * ContentClipboardProvider.
 */
export const useContentClipboard = () => useContext(ContentClipboardContext);

/**
 * Dispatch a new clipboard state to all components consuming this provider. Will return undefined
 * if not under a ContentClipboardProvider.
 */
export const useContentClipboardDispatch = () => useContext(ContentClipboardDispatch);

export interface ContentClipboardProviderProps {
  initialValue?: ContentClipboardContextValue;
  children?: ReactNode;
}

/**
 * Provider clipboard functionality, i.e. copy and paste, to content components that consumer this
 * provider.
 * @param initialValue
 * @param children
 * @constructor
 */
export const ContentClipboardProvider = ({
  initialValue,
  children,
}: ContentClipboardProviderProps) => {
  const [state, setState] = useState<ContentClipboardContextValue>(
    initialValue ?? { clipboardState: { state: 'empty' } }
  );
  return (
    <ContentClipboardContext.Provider value={state}>
      <ContentClipboardDispatch.Provider value={setState}>
        {children}
      </ContentClipboardDispatch.Provider>
    </ContentClipboardContext.Provider>
  );
};
